
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { subscriptionPlans, SubscriptionPlan } from '@/services/subscriptions';
import { getActiveSubscriptionPlans } from '@/services/subscriptionPlanService';
import { Button } from '@/components/ui/button';
import { Check, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';

const PricingPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<SubscriptionPlan[]>(subscriptionPlans);
  
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const dbPlans = await getActiveSubscriptionPlans();
        if (dbPlans && dbPlans.length > 0) {
          const convertedPlans = dbPlans.map(p => ({
            id: p.tier,
            name: p.name,
            tier: p.tier,
            price: p.price / 100,
            priceDisplay: p.price_display,
            description: p.description || '',
            features: p.features,
            leadLimit: p.lead_limit,
            recommended: p.is_recommended
          }));
          setPlans(convertedPlans);
        }
      } catch (error) {
        console.error('Error fetching plans:', error);
        // Keep using hardcoded plans as fallback
      }
    };
    
    fetchPlans();
  }, []);
  
  const handleGetStarted = () => {
    if (!user) {
      // Redirect to authentication if user is not logged in
      navigate('/auth', { state: { redirectTo: '/app' } });
      return;
    }
    
    // If user is logged in, go to app
    navigate('/app');
  };

  const freePlan = plans.find(plan => plan.tier === 'free');

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 py-24 px-4 sm:px-6">
      <div className="container mx-auto max-w-5xl">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4">Start growing your business today</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Get 20 leads per month for free. No credit card required.
          </p>
          
          {/* Primary CTA Button */}
          <Button 
            onClick={handleGetStarted}
            size="lg" 
            className="mt-8"
          >
            Start For Free <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
        
        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.filter(plan => plan.tier !== 'unlimited').map((plan) => (
            <div 
              key={plan.tier} 
              className={`rounded-lg border bg-card shadow-sm transition-all flex flex-col relative
                ${plan.recommended ? 'border-primary ring-1 ring-primary' : 'border-border'}
                ${plan.tier === 'free' ? 'border-green-500 ring-1 ring-green-500' : ''}
              `}
            >
              {plan.recommended && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <Badge variant="default" className="px-3 py-1">
                    Recommended
                  </Badge>
                </div>
              )}
              
              {plan.tier === 'free' && (
                <div className="absolute top-0 right-4 transform -translate-y-1/2">
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 px-3 py-1">
                    Start Now
                  </Badge>
                </div>
              )}
              
              <div className="p-6 border-b">
                <h3 className="text-2xl font-bold">{plan.name}</h3>
                <p className="text-muted-foreground text-sm mt-1">{plan.description}</p>
                <div className="mt-4 flex items-baseline">
                  <span className="text-4xl font-bold tracking-tight">{plan.priceDisplay}</span>
                  {plan.price > 0 && <span className="text-muted-foreground ml-1">/month</span>}
                </div>
              </div>
              
              <div className="p-6 flex-grow">
                <ul className="space-y-3">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="p-6 border-t bg-muted/20">
                {plan.tier === 'free' ? (
                  <Button 
                    onClick={handleGetStarted}
                    variant="success"
                    className="w-full"
                  >
                    Start Now
                  </Button>
                ) : (
                  <p className="text-center text-sm text-muted-foreground">
                    {user ? (
                      <Link to="/subscription" className="text-primary hover:underline">
                        Manage subscription in account settings
                      </Link>
                    ) : (
                      <span>Available after sign up</span>
                    )}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
        
        {/* FAQ Section */}
        <div className="mt-24">
          <h2 className="text-3xl font-bold mb-8 text-center">Frequently Asked Questions</h2>
          
          <div className="grid gap-6 md:grid-cols-2">
            <div className="rounded-lg border p-6 bg-card">
              <h3 className="text-lg font-medium mb-2">Do I need a credit card to sign up?</h3>
              <p className="text-muted-foreground">No, you can start with our Free plan without providing any payment information. Get 20 leads per month at no cost.</p>
            </div>
            
            <div className="rounded-lg border p-6 bg-card">
              <h3 className="text-lg font-medium mb-2">How do I upgrade my plan?</h3>
              <p className="text-muted-foreground">Once you've created your account, you can upgrade anytime from the Subscription page in your account settings.</p>
            </div>
            
            <div className="rounded-lg border p-6 bg-card">
              <h3 className="text-lg font-medium mb-2">Can I cancel my subscription?</h3>
              <p className="text-muted-foreground">Yes, you can cancel your paid subscription at any time and revert to the Free plan without losing your data.</p>
            </div>
            
            <div className="rounded-lg border p-6 bg-card">
              <h3 className="text-lg font-medium mb-2">Need more information?</h3>
              <p className="text-muted-foreground">Check our <Link to="/documentation" className="text-primary hover:underline">documentation</Link> or contact our support team for personalized assistance.</p>
            </div>
          </div>
          
          {/* Final CTA */}
          <div className="mt-16 text-center">
            <h3 className="text-2xl font-bold mb-4">Ready to grow your business?</h3>
            <Button 
              onClick={handleGetStarted}
              size="lg"
            >
              Get Started For Free <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
